import React, { useEffect, useState } from "react";
import { ArrowUp, Cookie, X, ShieldCheck } from "lucide-react";

/* Back-to-top: appears after scrolling, fixed bottom-center.
   Lifts above the cookie banner while the banner is visible (coordinated via
   event), and hides while the footer is in view so it never covers links. */
export function BackToTop() {
  const [show, setShow] = useState(false);
  const [bannerUp, setBannerUp] = useState(false);
  const [footerInView, setFooterInView] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    const onBanner = (e) => setBannerUp(!!(e.detail && e.detail.visible));
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("vsq:cookie-visibility", onBanner);
    onScroll();
    const footer = document.querySelector('[data-testid="site-footer"]');
    let obs = null;
    if (footer && "IntersectionObserver" in window) {
      obs = new IntersectionObserver(([entry]) => setFooterInView(entry.isIntersecting), { threshold: 0.08 });
      obs.observe(footer);
    }
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("vsq:cookie-visibility", onBanner);
      if (obs) obs.disconnect();
    };
  }, []);
  const visible = show && !footerInView;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      data-testid="back-to-top-button"
      className={`fixed left-1/2 -translate-x-1/2 z-40 w-12 h-12 rounded-full bg-[#16294D] text-white shadow-[0_12px_30px_rgba(22,41,77,0.45)] border-2 border-[#EEA415] flex items-center justify-center transition-all duration-300 hover:bg-[#1B3A6B] hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(238,164,21,0.4)] ${bannerUp ? "bottom-[290px] sm:bottom-[250px]" : "bottom-6"} ${visible ? "opacity-100 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}
    >
      <ArrowUp size={20} />
    </button>
  );
}

const CONSENT_KEY = "vsq_cookie_consent";

/* Cookie banner: shows only on first visit; saves chosen sections */
export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: true, marketing: false });

  useEffect(() => {
    if (!readConsent()) {
      const t = setTimeout(() => {
        setVisible(true);
        window.dispatchEvent(new CustomEvent("vsq:cookie-visibility", { detail: { visible: true } }));
      }, 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const save = (choice) => {
    const record = { ...choice, savedAt: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    // Fire marketing pixels only once the user grants marketing consent.
    try {
      if (record.marketing === true && typeof window.vsqLoadTikTokPixel === "function") {
        window.vsqLoadTikTokPixel();
      }
    } catch (e) {}
    window.dispatchEvent(new CustomEvent("vsq:consent-changed", { detail: record }));
    window.dispatchEvent(new CustomEvent("vsq:cookie-visibility", { detail: { visible: false } }));
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-50 px-3 sm:px-4 pb-3 sm:pb-5 pointer-events-none" data-testid="cookie-banner">
      <div className="pointer-events-auto mx-auto max-w-3xl card-vs !rounded-2xl p-4 sm:p-6 border-t-4 !border-t-[#EEA415] max-h-[60vh] overflow-y-auto">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FFF8EB] border border-[#F5C86B] flex items-center justify-center shrink-0">
            <Cookie size={18} className="text-[#C77F0A]" />
          </span>
          <div className="min-w-0">
            <div className="font-semibold text-[14px] sm:text-[15px] text-[#16294D]">We value your privacy</div>
            <p className="text-[12px] sm:text-[13px] text-slate-500 mt-1 leading-relaxed">
              ValueStackHQ uses cookies to run the platform, remember your session, and — with your
              permission — measure usage and marketing performance. Read our{" "}
              <a href="/legal/cookies" className="text-[#174C82] font-semibold underline">Cookie Policy</a>.
            </p>
          </div>
          <button onClick={() => save({ necessary: true, analytics: false, marketing: false, dismissed: true })} className="text-slate-400 hover:text-slate-600 shrink-0" aria-label="Dismiss" data-testid="cookie-dismiss-button"><X size={18} /></button>
        </div>
        {customize && (
          <div className="mt-4 space-y-2 rounded-xl bg-slate-50 border border-slate-200 p-3.5">
            {[
              ["necessary", "Strictly necessary", "Login, KYC session, security, consent memory. Always on.", true],
              ["analytics", "Analytics", "Anonymous usage measurement to improve the product.", false],
              ["marketing", "Marketing", "Campaign measurement and retargeting pixels.", false],
            ].map(([key, label, desc, locked]) => (
              <label key={key} className="flex items-start gap-3 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={locked ? true : prefs[key]}
                  disabled={locked}
                  onChange={(e) => setPrefs({ ...prefs, [key]: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#EEA415]"
                  data-testid={`cookie-toggle-${key}`}
                />
                <span><span className="font-semibold text-slate-800">{label}{locked && " (always on)"}</span><br /><span className="text-slate-500">{desc}</span></span>
              </label>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3 sm:mt-4">
          <button onClick={() => save({ necessary: true, analytics: true, marketing: true })} className="btn-primary-vs h-10 px-4 sm:px-5 text-[13px] font-semibold flex items-center gap-1.5" data-testid="cookie-accept-all-button"><ShieldCheck size={15} /> Accept all</button>
          <button onClick={() => save({ necessary: true, analytics: false, marketing: false })} className="btn-navy-vs h-10 px-4 sm:px-5 text-[13px] font-semibold" data-testid="cookie-reject-button">Reject optional</button>
          {customize
            ? <button onClick={() => save(prefs)} className="h-10 px-4 sm:px-5 text-[13px] font-semibold rounded-[10px] border border-[#174C82] text-[#16294D] hover:bg-[#FFF8EB] transition-colors" data-testid="cookie-save-choices-button">Save my choices</button>
            : <button onClick={() => setCustomize(true)} className="h-10 px-4 sm:px-5 text-[13px] font-semibold rounded-[10px] border border-slate-300 text-slate-600 hover:border-slate-400 transition-colors" data-testid="cookie-customize-button">Customize</button>}
        </div>
      </div>
    </div>
  );
}
